import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Zap,
  Grid3X3,
  ArrowLeft,
  Trophy,
  RefreshCcw,
  Activity,
  Layers,
  Grid,
  LayoutGrid,
  ArrowRight,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

// --- GAME 1: MEMORY (Hidden Balls) ---

const MemoryGame = ({ onComplete }) => {
  const [phase, setPhase] = useState(1); // 1: Balls, 2: Sequence, 3: Grid
  const [stage, setStage] = useState("init");
const [loading, setLoading] = useState(0);


  // Phase 1 States (Balls)
  const [balls, setBalls] = useState([]);
  const [targetBall, setTargetBall] = useState(null);

  // Phase 2 States (Sequence)
  const [sequence, setSequence] = useState([]);
  const [userSeq, setUserSeq] = useState([]);
  const [activeStep, setActiveStep] = useState(null);

  // Phase 3 States (Grid)
  const [gridTiles, setGridTiles] = useState([]);
  const [activeTiles, setActiveTiles] = useState([]);
  const [memTime, setMemTime] = useState(3000);

  const [isPaused, setIsPaused] = React.useState(false);

  // 2. Fixes 'setStatus' (Assuming you use this to switch between 'menu' and 'game')
  // If you are using React Router, you might use navigate("/") instead.
  const [status, setStatus] = React.useState("game");

  // 3. Ensure 'loading' is defined for the top bar
  const navigate = useNavigate();

  const [targetList, setTargetList] = useState([]); // Array of 2 targets
  const [currentTargetIdx, setCurrentTargetIdx] = useState(0);

  const initBalls = useCallback(() => {
    const colors = [
      { name: "Red", bg: "bg-red-500", shadow: "shadow-red-500/50" },
      { name: "Blue", bg: "bg-blue-500", shadow: "shadow-blue-500/50" },
      { name: "Green", bg: "bg-emerald-500", shadow: "shadow-emerald-500/50" },
      { name: "Yellow", bg: "bg-amber-500", shadow: "shadow-amber-500/50" },
      { name: "Purple", bg: "bg-purple-500", shadow: "shadow-purple-500/50" },
    ];
    const shuffled = [...colors].sort(() => Math.random() - 0.5);
    setBalls(shuffled);

    // Pick 2 different targets for the questions
    const targets = [...shuffled].sort(() => Math.random() - 0.5).slice(0, 2);
    setTargetList(targets);
    setCurrentTargetIdx(0);
    setStage("instruction"); // Start with instructions
  }, []);

//   useEffect(() => {
//   if (status === "menu") {
//     // Simulate a system scan/load
//     const timer = setTimeout(() => setLoading(74), 500); 
//     return () => clearTimeout(timer);
//   }
// }, [status]);

  const startMemoryPhase = () => {
    setStage("memorize");
    const time = 3500;
    setMemTime(time);

    setTimeout(() => {
      setStage("hide");
      setTimeout(() => setStage("question"), 800);
    }, time);
  };

  // --- PHASE 1: COLOR MEMORY ---

  // --- PHASE 2: SEQUENCE MEMORY ---
  const initSequence = useCallback(() => {
    // 5 steps across 6 possible cards
    const newSeq = Array.from({ length: 5 }, () =>
      Math.floor(Math.random() * 6),
    );
    setSequence(newSeq);
    setUserSeq([]);
    setStage("instruction"); // Start with instruction first
  }, []);

  const startSequence = () => {
    setStage("showing");
    sequence.forEach((step, i) => {
      setTimeout(
        () => {
          setActiveStep(step);
          setTimeout(() => setActiveStep(null), 400);
          if (i === sequence.length - 1)
            setTimeout(() => setStage("input"), 500);
        },
        (i + 1) * 700,
      );
    });
  };

  const initGrid = useCallback(() => {
    const indices = Array.from({ length: 16 }, (_, i) => i)
      .sort(() => Math.random() - 0.5)
      .slice(0, 10);

    const sequenceMap = {};
    indices.forEach((idx, i) => {
      sequenceMap[idx] = i + 1; // Correct numbers generated here
    });

    setGridTiles(sequenceMap);
    setUserSeq([]);
    setActiveTiles([]);
    setStage("instruction");
  }, []);

  const startGridGame = () => {
    // 1. Just reset progress and start the flash
    setActiveTiles([]);
    setStage("flash");

    // REMOVED: Step 2 and 3 (the "X" generation logic)
    // Because the numbers are already in gridTiles from initGrid()

    // 2. Set the timer to move to interaction mode
    setTimeout(() => {
      setStage("interact");
    }, 5000);
  };

  useEffect(() => {
    if (phase === 1) initBalls();
    if (phase === 2) initSequence();
    if (phase === 3) initGrid();
  }, [phase, initBalls, initSequence, initGrid]);

  // --- HANDLERS ---
  const handleBallClick = (i) => {
    if (stage !== "question") return;

    const clickedBall = balls[i];
    const currentTarget = targetList[currentTargetIdx];

    if (clickedBall.name === currentTarget.name) {
      if (currentTargetIdx === 0) {
        // Correct! Now ask for the second ball
        setCurrentTargetIdx(1);
      } else {
        // Both correct! Move to Phase 2
        setPhase(2);
      }
    } else {
      onComplete(false); // Wrong choice
    }
  };

  const handleSeqClick = (i) => {
    if (stage !== "input") return;
    const nextUserSeq = [...userSeq, i];
    if (sequence[userSeq.length] === i) {
      if (nextUserSeq.length === sequence.length) {
        setUserSeq([]);
        setPhase(3);
      } else setUserSeq(nextUserSeq);
    } else onComplete(false);
  };

  // --- HANDLER FOR PHASE 3 ---
  const handleGridClick = (i) => {
    if (stage !== "interact") return;

    const targetNumber = activeTiles.length + 1; // We are looking for 1, then 2, etc.
    const clickedNumber = gridTiles[i];

    if (clickedNumber === targetNumber) {
      const nextActive = [...activeTiles, i];
      setActiveTiles(nextActive);

      // Check if Protocol is complete
      if (nextActive.length === 5) {
        onComplete(true);
      }
    } else {
      // If they click a wrong tile OR a correct tile out of order
      onComplete(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 select-none">
      <div className="text-center mb-8">
        <div className="flex justify-center gap-1 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 w-8 rounded-full transition-all duration-500 ${phase >= s ? "bg-blue-500" : "bg-zinc-800"}`}
            />
          ))}
        </div>
        <p className="text-blue-500 font-black uppercase tracking-[0.3em] text-[10px] mb-1">
          {phase === 1
            ? "Protocol 01: Spatial"
            : phase === 2
              ? "Protocol 02: Chrono"
              : "Protocol 03: Pattern"}
        </p>
        <h2 className="text-xl font-black uppercase italic tracking-tighter">
          {phase === 1 &&
            (stage === "question"
              ? `Where is the ${targetBall?.name} ball?`
              : "Scan Positions")}
          {phase === 2 &&
            (stage === "showing" ? "Observe Sequence" : "Repeat Pattern")}
          {phase === 3 &&
            (stage === "flash" ? "Memorize Grid" : "Restore Signal")}
        </h2>
      </div>

      {/* PHASE 1 UI: BALLS */}
      {phase === 1 && (
        <div className="fixed inset-0 z-[100] bg-[#050505] text-white flex flex-col items-center justify-between overflow-hidden select-none px-4 py-4">
          {/* 1. BACKGROUND LAYER (Solid & Deep) */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#1e3a8a_0%,_transparent_85%)] opacity-30" />
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-50" />
          </div>

          {/* 2. TOP HUD (Glass Effect) */}
          <div className="w-full max-w-7xl flex justify-between items-center z-10 bg-white/5 backdrop-blur-md border-b border-white/10 px-4 py-3 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-[0_0_8px_#3b82f6]" />
              <p className="text-[10px] tracking-widest font-bold uppercase">
                Neuralis_v1
              </p>
            </div>
            <div className="flex flex-col items-end">
              <p className="text-[8px] text-zinc-200 uppercase font-black mb-1">
                System_Sync
              </p>
              <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 shadow-[0_0_10px_#3b82f6] transition-all ease-linear"
                  style={{
                    width:
                      stage === "memorize"
                        ? "100%"
                        : stage === "question"
                          ? "100%"
                          : "0%",
                    transitionDuration:
                      stage === "memorize" ? `${memTime}ms` : "300ms",
                  }}
                />
              </div>
            </div>
          </div>

          {/* 3. MAIN GAME CONTENT */}
          <div className="relative z-10 w-full flex flex-col items-center justify-center flex-1 min-h-0 space-y-4">
            {/* INSTRUCTION MODAL (Heavy Glass Effect) */}
            {stage === "instruction" && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xl p-6">
                <div className="text-center p-8 border border-white/20 bg-zinc-900/50 rounded-2xl max-w-xs w-full shadow-2xl">
                  <Box className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-black uppercase mb-2">Ready?</h3>
                  <p className="text-zinc-400 text-xs font-medium uppercase tracking-tighter leading-relaxed mb-8">
                    Memorize the colors. <br /> Find them when asked.
                  </p>
                  <button
                    onClick={startMemoryPhase}
                    className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-widest text-xs rounded-lg transition-all active:scale-95 shadow-lg shadow-blue-600/20"
                  >
                    Start Game
                  </button>
                </div>
              </div>
            )}

            {/* HEADER: Easy English Questions */}
            <header className="text-center px-2">
              <p className="text-[10px] tracking-[0.3em] text-blue-500 font-black uppercase mb-2">
                {stage === "memorize" ? "Step 1: Remember" : "Step 2: Find"}
              </p>
              <h2 className="text-3xl sm:text-5xl md:text-7xl font-black tracking-tighter uppercase leading-tight italic">
                {stage === "memorize" ? (
                  "Memorize!"
                ) : stage === "question" ? (
                  <div className="flex flex-col items-center">
                    <span className="text-zinc-200 text-sm md:text-xl not-italic tracking-normal mb-1">
                      Tap the square that was:
                    </span>
                    <span
                      className={`${targetList[currentTargetIdx]?.bg.replace("bg-", "text-")} drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]`}
                    >
                      {targetList[currentTargetIdx]?.name}
                    </span>
                  </div>
                ) : (
                  "Standby"
                )}
              </h2>
            </header>

            {/* GRID: Compact & Solid Cards */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 w-full max-w-md px-2">
              {balls.map((b, i) => (
                <button
                  key={i}
                  onClick={() => handleBallClick(i)}
                  disabled={stage !== "question"}
                  className={`aspect-square rounded-xl border-2 transition-all duration-300 flex items-center justify-center relative shadow-inner
              ${
                stage === "question"
                  ? "border-white/10 bg-white/5 hover:bg-white/10 active:scale-90"
                  : "border-zinc-800 bg-zinc-900/50"
              }
            `}
                >
                  <span className="absolute top-2 left-2 text-[8px] font-bold text-zinc-600">
                    #{i + 1}
                  </span>

                  <div className="absolute inset-0 flex items-center justify-center p-2">
                    <div
                      className={`
                w-full h-full ${b.bg} rounded-lg shadow-2xl
                transition-all duration-500 cubic-bezier(0.17, 0.55, 0.55, 1)
                ${stage === "memorize" ? "scale-100 opacity-100" : "scale-0 opacity-0"}
              `}
                    />
                  </div>

                  {/* Subtle center icon for empty nodes */}
                  {stage === "question" && (
                    <div className="w-1 h-1 bg-white/20 rounded-full" />
                  )}
                </button>
              ))}
            </div>

            {/* ABORT BUTTON */}
            <button
              onClick={() => navigate("/game/start")}
              className="text-[10px] tracking-widest text-zinc-600 hover:text-red-500 uppercase font-black transition-colors pt-4 active:scale-95"
            >
              [ Quit Session ]
            </button>
          </div>

          {/* 4. FOOTER STATUS (Glassy) */}
          <div className="w-full max-w-md bg-white/5 border border-white/10 py-2 px-6 rounded-full flex justify-between items-center opacity-60">
            <span className="text-[8px] font-bold text-zinc-400 uppercase">
              Nodes: {balls.length}
            </span>
            <span className="text-[8px] font-bold text-blue-500 uppercase animate-pulse">
              System Active
            </span>
          </div>
        </div>
      )}

      {/* PHASE 2 UI: SEQUENCE */}
      {phase === 2 && (
        <div className="fixed inset-0 z-[100] bg-[#050505] text-white flex flex-col items-center justify-between overflow-hidden select-none px-4 py-3 md:py-6">
          {/* 1. ATMOSPHERIC BACKGROUND */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#1e3a8a_0%,_transparent_85%)] opacity-30" />
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-50" />
          </div>

          {/* 2. TOP HUD (Compact for Mobile) */}
          <div className="w-full max-w-5xl flex justify-between items-center z-10 bg-white/5 backdrop-blur-md border border-white/10 px-4 py-2 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full shadow-[0_0_8px_#3b82f6]" />
              <p className="text-[9px] tracking-widest font-bold uppercase">
                Chrono_Sync
              </p>
            </div>
            <div className="flex flex-col items-end">
              <div className="w-20 h-1 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 shadow-[0_0_10px_#3b82f6] transition-all duration-300"
                  style={{ width: `${(userSeq.length / 5) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* 3. MAIN GAME CONTENT (No-Scroll Container) */}
          <div className="relative z-10 w-full flex flex-col items-center justify-center flex-1 min-h-0 py-2">
            {/* INSTRUCTION MODAL */}
            {stage === "instruction" && (
              <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-xl flex flex-col items-center justify-center p-6">
                <div className="text-center p-6 border border-white/20 bg-zinc-900/50 rounded-2xl max-w-xs w-full shadow-2xl">
                  <Layers className="w-10 h-10 text-blue-500 mx-auto mb-4 animate-pulse" />
                  <h3 className="text-xl font-black uppercase mb-2">
                    Repeat Pattern
                  </h3>
                  <p className="text-zinc-400 text-[10px] font-medium uppercase tracking-widest mb-6">
                    Watch the sequence. <br /> Tap in the same order.
                  </p>
                  <button
                    onClick={startSequence}
                    className="w-full py-4 bg-blue-600 text-white font-black uppercase tracking-widest text-[10px] rounded-lg active:scale-95 shadow-lg shadow-blue-600/20"
                  >
                    Start Sync
                  </button>
                </div>
              </div>
            )}

            {/* HEADER: Simplified */}
            <header className="text-center mb-4">
              <p className="text-[9px] tracking-[0.3em] text-blue-500 font-black uppercase mb-1">
                {stage === "playback" ? "Observe" : "Replay"}
              </p>
              <h2 className="text-3xl md:text-6xl font-black tracking-tighter uppercase italic">
                {stage === "playback" ? (
                  <span className="animate-pulse">Watching...</span>
                ) : stage === "input" ? (
                  <span>
                    Your <span className="text-blue-500">Turn</span>
                  </span>
                ) : (
                  "Ready?"
                )}
              </h2>
            </header>

            {/* 6-Card Grid: Mobile Optimized 3x2 Layout */}
            <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 md:gap-4 w-full max-w-md lg:max-w-4xl px-2">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  onClick={() => handleSeqClick(i)}
                  disabled={stage !== "input"}
                  className={`aspect-square rounded-xl border-2 transition-all duration-300 flex items-center justify-center relative shadow-inner
              ${
                activeStep === i
                  ? "bg-blue-600 border-blue-400 scale-105 shadow-[0_0_25px_rgba(59,130,246,0.5)] z-20"
                  : "bg-zinc-900/50 border-zinc-800"
              }
              ${stage === "input" ? "active:scale-90 active:bg-blue-500/30" : "cursor-default"}`}
                >
                  <span
                    className={`absolute top-1.5 left-2 text-[7px] font-bold uppercase
              ${activeStep === i ? "text-white" : "text-zinc-600"}`}
                  >
                    N_{i + 1}
                  </span>

                  <Layers
                    className={`w-6 h-6 md:w-12 md:h-12 transition-all duration-500
              ${activeStep === i ? "text-white" : "text-zinc-800 opacity-30"}`}
                  />

                  {/* Visual feedback for playback */}
                  {activeStep === i && (
                    <div className="absolute inset-0 bg-blue-400/10 animate-ping rounded-xl pointer-events-none" />
                  )}
                </button>
              ))}
            </div>

            {/* QUIT BUTTON: Tucked in */}
            <button
              onClick={() => navigate("/")}
              className="mt-6 text-[9px] tracking-[0.4em] text-zinc-700 hover:text-red-500 uppercase font-black transition-colors py-2 active:scale-95"
            >
              [ Abort ]
            </button>
          </div>

          {/* 4. BOTTOM STATUS (Slim Glassy Design) */}
          <div className="w-full max-w-xs bg-white/5 border border-white/10 py-2.5 px-5 rounded-xl flex flex-col items-center gap-1.5 opacity-90 mb-2">
            <div className="w-full flex justify-between text-[8px] font-bold uppercase tracking-widest text-zinc-200">
              <span>Link_Stability</span>
              <span className="text-blue-500">{userSeq.length}/5</span>
            </div>
            <div className="flex gap-1.5 w-full">
              {Array.from({ length: 5 }).map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1 flex-grow rounded-full transition-all duration-500 
            ${userSeq.length > idx ? "bg-blue-500 shadow-[0_0_5px_#3b82f6]" : "bg-zinc-800"}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PHASE 3 UI: GRID FLASH */}

      {/* // --- RENDER FOR PHASE 3 --- */}
      {phase === 3 && (
        <div className="fixed inset-0 z-[100] bg-[#050505] text-white flex flex-col items-center justify-between overflow-hidden select-none px-4 py-4">
          {/* 1. ATMOSPHERIC BACKGROUND */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#1e3a8a_0%,_transparent_85%)] opacity-30" />
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-50" />
          </div>

          {/* 2. TOP HUD (Glassy) */}
          <div className="w-full max-w-7xl flex justify-between items-center z-10 bg-white/5 backdrop-blur-md border-b border-white/10 px-4 py-3 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full shadow-[0_0_8px_#3b82f6]" />
              <p className="text-[10px] tracking-widest font-bold uppercase">
                Grid_Matrix_v3
              </p>
            </div>
            <div className="flex flex-col items-end">
              <p className="text-[8px] text-zinc-200 uppercase font-black mb-1">
                Neural_Map
              </p>
              <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 shadow-[0_0_10px_#3b82f6] transition-all duration-500"
                  style={{ width: `${(activeTiles.length / 5) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* 3. MAIN GAME CONTENT */}
          <div className="relative z-10 w-full flex flex-col items-center justify-center flex-1 min-h-0 space-y-4">
            {/* INSTRUCTION MODAL - Easy English */}
            {stage === "instruction" && (
              <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-xl flex flex-col items-center justify-center p-6">
                <div className="text-center p-6 border border-white/20 bg-zinc-900/50 rounded-2xl max-w-xs w-full shadow-2xl">
                  <LayoutGrid className="w-10 h-10 text-blue-500 mx-auto mb-4 animate-pulse" />
                  <h3 className="text-xl font-black uppercase mb-2">
                    Find the Boxes
                  </h3>
                  <p className="text-zinc-400 text-[10px] font-medium uppercase tracking-widest mb-6 leading-relaxed">
                    Watch the grid. <br /> Remember the numbers 1 to 10. <br />{" "}
                    Tap them in order.
                  </p>
                  <button
                    onClick={startGridGame}
                    className="w-full py-4 bg-blue-600 text-white font-black uppercase tracking-widest text-[10px] rounded-lg active:scale-95 shadow-lg shadow-blue-600/20"
                  >
                    Begin Scan
                  </button>
                </div>
              </div>
            )}

            {/* HEADER: Easy English */}
            <header className="text-center px-2">
              <p className="text-[10px] tracking-[0.3em] text-blue-500 font-black uppercase mb-2">
                {stage === "flash" ? "Step 1: Look" : "Step 2: Tap"}
              </p>
              <h2 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tighter uppercase leading-tight italic">
                {stage === "flash" ? (
                  <span className="animate-pulse">Memorizing...</span>
                ) : (
                  <span>
                    Find the <span className="text-blue-500">Boxes</span>
                  </span>
                )}
              </h2>
            </header>

            {/* 4x4 GRID */}
            <div className="grid grid-cols-4 gap-2 md:gap-4 w-full max-w-[min(85vw,400px)] aspect-square">
              {Array(16)
                .fill(0)
                .map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleGridClick(i)}
                    disabled={stage !== "interact"}
                    className={`group aspect-square rounded-xl border-2 transition-all duration-300 flex items-center justify-center relative overflow-hidden
              ${
                stage === "flash" && gridTiles[i]
                  ? "bg-blue-600 border-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.4)]"
                  : "bg-zinc-900/50 border-zinc-800"
              }
              ${
                stage === "interact" && activeTiles.includes(i)
                  ? "bg-blue-500/20 border-blue-500 shadow-[inset_0_0_15px_rgba(59,130,246,0.2)]"
                  : ""
              }
              ${stage === "interact" ? "active:scale-90" : "cursor-default"}`}
                  >
                    {/* Coordinate label */}
                    <span
                      className={`absolute top-1 left-1.5 text-[6px] md:text-[8px] font-mono font-bold
              ${gridTiles[i] && stage === "flash" ? "text-white" : "text-zinc-700"}`}
                    >
                      0{i + 1}
                    </span>

                    {/* The actual symbol/tile */}
                    <span
                      className={`text-xl md:text-4xl font-black italic transition-all duration-500
  ${stage === "flash" && gridTiles[i] ? "opacity-100 scale-100" : "opacity-0 scale-50"}
  ${stage === "interact" && activeTiles.includes(i) ? "opacity-100 text-white scale-110" : ""}
`}
                    >
                      {/* If gridTiles[i] exists, it will show the number (1-5). 
    If it's undefined, it shows nothing. 
  */}
                      {gridTiles[i]}
                    </span>

                    {/* Interaction Glow */}
                    {activeTiles.includes(i) && (
                      <div className="absolute inset-0 border-2 border-blue-500 animate-pulse opacity-40 rounded-xl" />
                    )}
                  </button>
                ))}
            </div>

            {/* QUIT BUTTON */}
            <button
              onClick={() => navigate("/")}
              className="text-[10px] tracking-widest text-zinc-600 hover:text-red-500 uppercase font-black transition-colors pt-2 active:scale-95"
            >
              [ Quit Session ]
            </button>
          </div>

          {/* 4. BOTTOM STATUS BAR */}
          <div className="w-full max-w-md bg-white/5 border border-white/10 py-3 px-6 rounded-2xl flex flex-col items-center gap-2 opacity-90 mb-2">
            <div className="w-full flex justify-between text-[10px] font-bold uppercase tracking-widest">
              <span className="text-zinc-200 italic">Progress</span>
              <span className="text-blue-500">
                Found {activeTiles.length} / 5
              </span>
            </div>
            <div className="flex gap-1.5 w-full">
              {[1, 2, 3, 4, 5].map((step) => (
                <div
                  key={step}
                  className={`h-1 flex-grow rounded-full transition-all duration-500 
            ${activeTiles.length >= step ? "bg-blue-500 shadow-[0_0_8px_#3b82f6]" : "bg-zinc-800"}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// --- GAME 2: FOCUS (Color Trick) ---
const FocusGame = ({ onComplete }) => {
  const [data, setData] = useState({ text: "", colorHex: "", borderHex: "", answerVal: "", mode: "COLOR", sequence: [] });
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(100);
  const [glitch, setGlitch] = useState(false);
  const [userSequence, setUserSequence] = useState([]); 
  const [isPaused, setIsPaused] = useState(true); // New state to handle transitions
  const isRoundActive = useRef(true);

  const protocols = [
    { name: "RED", hex: "text-red-500", border: "border-red-500", val: "red", bg: "bg-red-500" },
    { name: "BLUE", hex: "text-blue-400", border: "border-blue-400", val: "blue", bg: "bg-blue-400" },
    { name: "GREEN", hex: "text-emerald-400", border: "border-emerald-400", val: "green", bg: "bg-emerald-400" },
    { name: "YELLOW", hex: "text-yellow-400", border: "border-yellow-400", val: "yellow", bg: "bg-yellow-400" },
  ];

  const next = useCallback(() => {
    isRoundActive.current = true;
    setGlitch(true);
    setTimeLeft(100);
    setUserSequence([]); 
    setTimeout(() => setGlitch(false), 100);

    if (score < 10) {
      const currentMode = Math.random() > 0.5 ? "COLOR" : "MEANING";
      const textIdx = Math.floor(Math.random() * 4);
      let colorIdx = (textIdx + Math.floor(Math.random() * 3) + 1) % 4;
      let borderIdx = (colorIdx + Math.floor(Math.random() * 3) + 1) % 4;
      setData({ 
        text: protocols[textIdx].name, colorHex: protocols[colorIdx].hex, borderHex: protocols[borderIdx].border,
        answerVal: currentMode === "COLOR" ? protocols[colorIdx].val : protocols[textIdx].val,
        mode: currentMode
      });
    } else if (score < 20) {
      const n1 = Math.floor(Math.random() * 9) + 1;
      const n2 = Math.floor(Math.random() * 9) + 1;
      const op = Math.random() > 0.5 ? "+" : "-";
      setData({
        text: `${n1}${op}${n2}`,
        colorHex: protocols[Math.floor(Math.random() * 4)].hex,
        borderHex: protocols[Math.floor(Math.random() * 4)].border,
        answerVal: (op === "+" ? n1 + n2 : Math.abs(n1 - n2)).toString(),
        mode: "CALCULATE"
      });
    } else {
      const s1 = Math.floor(Math.random() * 4);
      const s2 = Math.floor(Math.random() * 4);
      setData({
        text: `${protocols[s1].name} > ${protocols[s2].name}`,
        colorHex: protocols[Math.floor(Math.random() * 4)].hex,
        borderHex: "border-white/20",
        sequence: [protocols[s1].val, protocols[s2].val],
        mode: "GHOST"
      });
    }
  }, [score]);

  useEffect(() => {
    if (isPaused) return; // Stop timer during transitions

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          if (isRoundActive.current) onComplete(false);
          return 0;
        }
        return prev - (score < 10 ? 1.7 : score < 20 ? 3.2 : 2.7); 
      });
    }, 50);
    return () => clearInterval(timer);
  }, [data, onComplete, score, isPaused]);

  useEffect(() => { next(); }, [next]);

  const handleInput = (selectedVal) => {
    if (isPaused) return;

    if (score < 20) {
      isRoundActive.current = false;
      if (selectedVal === data.answerVal) {
        const nextScore = score + 1;
        setScore(nextScore);
        // Gate Level 2 and Level 3 starts
        if (nextScore === 10 || nextScore === 20) {
            setIsPaused(true);
        }
        next();
      } else onComplete(false);
    } else {
      const newSeq = [...userSequence, selectedVal];
      setUserSequence(newSeq);
      if (selectedVal !== data.sequence[newSeq.length - 1]) {
        onComplete(false);
        return;
      }
      if (newSeq.length === data.sequence.length) {
        isRoundActive.current = false;
        if (score >= 29) onComplete(true);
        else {
          setScore((s) => s + 1);
          next();
        }
      }
    }
  };

  const mathOptions = useMemo(() => {
    if (score < 10 || score >= 20) return [];
    const correct = parseInt(data.answerVal);
    const set = new Set([correct]);
    while(set.size < 4) set.add(Math.floor(Math.random() * 18));
    return Array.from(set).sort(() => Math.random() - 0.5);
  }, [data.answerVal, score]);

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-between bg-[#050505] text-white px-6 py-10 overflow-hidden touch-none font-sans">
      
      {/* LEVEL START OVERLAY */}
{/* LEVEL START OVERLAY */}
{isPaused && (
  <div 
    onClick={() => setIsPaused(false)}
    className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 backdrop-blur-xl cursor-pointer"
  >
    <div className="text-center space-y-6 px-10">
      <div className="space-y-1">
        <h2 className="text-zinc-200 text-[10px] tracking-[0.4em] uppercase font-bold">System Ready</h2>
        <h1 className="text-6xl font-black italic tracking-tighter">
          {score === 0 ? "LEVEL 1" : score === 10 ? "LEVEL 2" : "LEVEL 3"}
        </h1>
      </div>

      {/* EASY INSTRUCTIONS */}
      <div className="py-4 border-y border-white/10">
        <p className="text-sm font-medium text-zinc-300">
          {score === 0 && "Match the color or the word. Fast."}
          {score === 10 && "Solve the math. Pick the right number."}
          {score === 20 && "Memorize the sequence. Tap in order."}
        </p>
      </div>

      <div className="pt-4">
        <p className="text-blue-400 text-[11px] font-black tracking-[0.2em] uppercase animate-pulse">
          READY? TAP TO START
        </p>
      </div>
    </div>
  </div>
)}
      {/* 1. TOP HUD */}
      <div className="w-full max-w-md">
        <div className="flex justify-between items-end mb-2">
           <div className="flex flex-col">
             <span className="text-[8px] text-zinc-200 uppercase tracking-[0.2em]">
               {score < 10 ? "Level 1: Sensory" : score < 20 ? "Level 2: Logic" : "Level 3: Ghost Grid"}
             </span>
             <span className="text-sm font-black italic">{score}/30</span>
           </div>
           <span className={`text-[10px] font-black uppercase ${score >= 20 ? 'text-red-500 animate-pulse' : 'text-zinc-600'}`}>
             {score >= 20 ? "Neural Overload" : "Neural Link Timer"}
           </span>
        </div>
        <div className="w-full h-1.5 bg-zinc-900 rounded-full">
          <div className="h-full bg-white transition-all duration-75 linear" style={{ width: `${timeLeft}%` }} />
        </div>
      </div>

      {/* 2. CENTER PIECE */}
      <div className={`flex-1 flex flex-col items-center justify-center w-full transition-all ${glitch ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}>
        <div className="bg-white/5 border border-white/10 px-6 py-2 rounded-full mb-8">
           <p className="text-xs font-bold tracking-[0.1em] uppercase text-center">
             {score < 20 ? (
               <>TAP THE <span className={data.mode === "COLOR" ? "text-blue-400" : data.mode === "MEANING" ? "text-yellow-400" : "text-red-500"}>
                 {data.mode === "COLOR" ? "ACTUAL COLOR" : data.mode === "MEANING" ? "WRITTEN WORD" : "CORRECT RESULT"}
               </span></>
             ) : (
               <span className="text-red-400 animate-pulse">TAP COLORS IN THE SHOWN ORDER</span>
             )}
           </p>
        </div>
        
        <div className={`relative inline-flex items-center justify-center p-10 md:p-16 border-[6px] rounded-[2.5rem] transition-all duration-300 ${data.borderHex} bg-zinc-900/10`}>
           <h1 className={`text-4xl md:text-6xl font-black tracking-tighter uppercase text-center ${data.colorHex}`}>
             {data.text}
           </h1>
        </div>
        
        <p className="mt-8 text-[9px] text-zinc-600 uppercase tracking-[0.5em] font-bold">
          {score < 20 ? "Ignore the border" : "Labels Offline"}
        </p>
      </div>

      {/* 3. DYNAMIC BUTTON GRID */}
      <div className="w-full max-w-sm grid grid-cols-2 gap-4">
        {score < 10 || score >= 20 ? (
          protocols.map((p) => (
            <button
              key={p.val}
              onClick={() => handleInput(p.val)}
              className="group relative py-6 bg-zinc-900/80 border border-white/5 rounded-2xl transition-all active:scale-90"
            >
              <div className="flex flex-col items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${p.bg} shadow-lg`} />
                <span className={`text-xs font-black uppercase tracking-widest transition-opacity duration-300 ${score >= 20 ? 'opacity-0' : 'opacity-100'}`}>
                  {p.name}
                </span>
              </div>
            </button>
          ))
        ) : (
          mathOptions.map((num) => (
            <button
              key={num}
              onClick={() => handleInput(num.toString())}
              className="py-8 bg-white/5 border border-white/10 rounded-2xl active:bg-white active:text-black text-2xl font-black transition-colors"
            >
              {num}
            </button>
          ))
        )}
      </div>

      <footer className="mt-8">
        <p className="text-[7px] text-zinc-800 font-bold uppercase tracking-[1em]">Omore.Games | Neuralis</p>
      </footer>
    </div>
  );
};
// --- GAME 3: SPEED (Number Scan) ---
const SpeedGame = ({ onComplete }) => {
  const [nums, setNums] = useState([]);
  const [nextNum, setNextNum] = useState(1);
  const [level, setLevel] = useState(3);
  const [points, setPoints] = useState(0); // Added points state
  const [isPaused, setIsPaused] = useState(true);
  const [timeLeft, setTimeLeft] = useState(15);
  const [showMemory, setShowMemory] = useState(true);

  // Level 3 Memory Flicker Logic
  useEffect(() => {
    if (level === 3 && !isPaused) {
      setShowMemory(true);
      const timer = setTimeout(() => setShowMemory(false), 800);
      return () => clearTimeout(timer);
    }
  }, [nextNum, level, isPaused]);

  // Timer Logic
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          onComplete(false);
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);
    return () => clearInterval(timer);
  }, [isPaused, onComplete]);

  const initLevel = useCallback(() => {
    const newNums = Array.from({ length: 16 }, (_, i) => i + 1).sort(() => Math.random() - 0.5);
    setNums(newNums);
    setNextNum(1);
    if (level === 1) setTimeLeft(15);
    if (level === 2) setTimeLeft(20);
    if (level === 3) setTimeLeft(17);
  }, [level]);

  useEffect(() => { initLevel(); }, [initLevel, level]);

  const handleChoice = (n) => {
    if (isPaused) return;

    if (n === nextNum) {
      setPoints(prev => prev + 10); // Add 10 points for every correct tap
      
      if (n === 16) {
        setPoints(prev => prev + 100); // Bonus 100 points for clearing a level
        if (level === 3) onComplete(true);
        else {
          setLevel(prev => prev + 1);
          setIsPaused(true);
        }
      } else {
        setNextNum(n + 1);
        if (level >= 2) {
          setNums(prev => [...prev].sort(() => Math.random() - 0.5));
        }
      }
    } else {
      onComplete(false);
    }
  };

  const maxTime = level === 1 ? 15 : level === 2 ? 20 : 25;
  const timerPercentage = (timeLeft / maxTime) * 100;

  return (
    <div className="relative flex flex-col items-center justify-center h-full w-full px-6 bg-[#050505] overflow-hidden">
      
      {/* LEVEL TRANSITION OVERLAY */}
{/* LEVEL TRANSITION OVERLAY */}
      {isPaused && (
        <div 
          onClick={() => setIsPaused(false)} 
          className="absolute inset-0 z-50 bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center cursor-pointer text-center p-8 transition-all"
        >
          {/* TOP TAG */}
          <div className="mb-2 px-3 py-1 bg-zinc-900 border border-white/10 rounded-full">
            <h2 className="text-zinc-200 text-[8px] tracking-[0.4em] uppercase font-black">
              System Ready: Phase {level}
            </h2>
          </div>

          {/* MAIN TITLE */}
          <h1 className="text-6xl font-black italic tracking-tighter text-white mb-6">
            {level === 1 && "STAY STILL"}
            {level === 2 && "MOVE FAST"}
            {level === 3 && "FLASH MEMORY"}
          </h1>

          {/* EASY INSTRUCTIONS */}
          <div className="space-y-4 mb-10 max-w-[240px]">
            <div className="h-[2px] w-12 bg-blue-500 mx-auto" />
            
            <div className="text-blue-400 text-xs font-bold uppercase tracking-[0.1em] leading-relaxed">
              {level === 1 && (
                <p>Numbers stay in one place. <br/>Tap <span className="text-white">1 to 16</span> as fast as you can.</p>
              )}
              {level === 2 && (
                <p>The grid <span className="text-white">shuffles</span> every time you tap. Don't let it confuse you.</p>
              )}
{level === 3 && (
  <div className="space-y-2">
    <p>We show <span className="text-white">3 numbers</span> for a split second.</p>
    <p className="text-blue-400">Tap the <span className="text-white font-black underline italic">first</span> one to see the next set.</p>
  </div>
)}
            </div>

            <p className="text-zinc-200 text-[9px] font-black uppercase tracking-widest">
              Limit: {level === 1 ? "15s" : level === 2 ? "20s" : "17s"}
            </p>
          </div>

          {/* START BUTTON */}
          <div className="group flex flex-col items-center gap-4">
            <div className="px-10 py-4 bg-white text-black rounded-full text-[11px] font-black uppercase tracking-[0.3em] active:scale-90 transition-transform">
              Connect Link
            </div>
            <p className="text-zinc-700 text-[8px] font-bold uppercase tracking-[0.5em] animate-pulse">
              Tap Anywhere to Start
            </p>
          </div>
        </div>
      )}

      {/* TOP HUD: SCORE & TIMER */}
      <div className="w-full max-w-[320px] mb-6 space-y-4">
        <div className="flex justify-between items-center px-1">
           <div className="flex flex-col">
              <span className="text-[8px] text-zinc-200 font-black uppercase tracking-widest">Neural Score</span>
              <span className="text-xl font-black text-white italic">{points.toLocaleString()}</span>
           </div>
           <div className="text-right">
              <span className="text-[8px] text-zinc-200 font-black uppercase tracking-widest">Stability</span>
              <p className={`text-xl font-black italic ${timeLeft < 5 ? 'text-red-500 animate-pulse' : 'text-blue-500'}`}>
                {timeLeft.toFixed(1)}s
              </p>
           </div>
        </div>
        
        {/* TIMER BAR */}
        <div className="w-full h-1 bg-zinc-900 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-100 linear ${timeLeft < 5 ? 'bg-red-500' : 'bg-blue-500'}`} 
            style={{ width: `${timerPercentage}%` }} 
          />
        </div>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-5xl font-black italic uppercase text-white tracking-tighter">
          NEXT: <span className="text-blue-500">{nextNum}</span>
        </h2>
      </div>

      {/* THE GRID */}
      <div className="grid grid-cols-4 gap-3 w-full max-w-[320px] aspect-square">
        {nums.map((n) => {
          const isVisible = level === 3 
            ? (n >= nextNum && n <= nextNum + 2 && showMemory)
            : true;

          return (
            <button
              key={n}
              onClick={() => handleChoice(n)}
              className={`group relative flex items-center justify-center text-xl font-black rounded-2xl border-2 transition-all duration-200 active:scale-90
              ${n < nextNum 
                ? "bg-blue-600/5 border-blue-500/10 text-blue-900/40" 
                : "bg-zinc-900 border-white/5 text-white active:bg-blue-500 active:border-blue-400"}
              `}
            >
              <span className={`transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-12 opacity-20">
         <p className="text-[8px] font-bold uppercase tracking-[1em] text-white">Omore.Games | Speed Protocol</p>
      </div>
    </div>
  );
};
// --- MAIN UI ---
export default function NeuralisSuite() {
  const [game, setGame] = useState(null);
  const [status, setStatus] = useState("menu");
  const [timer, setTimer] = useState(0);
  const timerRef = useRef();
  const [loading, setLoading] = useState(0);

  useEffect(() => {
  if (status === "menu") {
    const interval = setInterval(() => {
      // Fluctuate between 70 and 75%
      setLoading(Math.floor(Math.random() * 5) + 100);
    }, 1000);
    return () => clearInterval(interval);
  }
}, [status]);

  const handleResult = (success) => {
    clearInterval(timerRef.current);
    setStatus(success ? "win" : "fail");
  };

  const start = (type) => {
    setGame(type);
    setStatus("playing");
    setTimer(0);
    timerRef.current = setInterval(() => setTimer((t) => t + 1), 1000);
  };

  return (
    <div className="fixed inset-0 h-[100dvh] w-screen bg-[#050505] text-white flex flex-col overflow-hidden touch-none overscroll-none select-none z-[9999]">
      <Helmet>
        <title>Neuralis Focus</title>
      </Helmet>

      {/* HEADER */}
      {/* <nav className="h-16 border-b border-white/5 flex items-center justify-between px-6 shrink-0 bg-black">
        <div className="flex items-center gap-4">
          {status !== "menu" && (
            <ArrowLeft
              className="w-5 h-5 text-zinc-200"
              onClick={() => setStatus("menu")}
            />
          )}
          <span className="text-[10px] font-black uppercase tracking-[0.4em] italic text-blue-500">
            Neuralis
          </span>
        </div>
        {status === "playing" && (
          <div className="flex items-center gap-2 font-mono text-blue-500 text-sm font-bold">
            <Activity className="w-4 h-4 animate-pulse" /> {timer}s
          </div>
        )}
      </nav> */}

      {/* CENTER STAGE */}
      <main className="flex-grow relative overflow-hidden">
{status === "menu" && (
  <div className="fixed inset-0 z-[100] bg-[#0f1115] text-[#e2e8f0] flex flex-col items-center justify-between overflow-hidden select-none px-6 py-10">
    
    {/* 1. SOFT BACKGROUND GRADIENT */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-white/5 blur-[120px] rounded-full" />
    </div>

    {/* 2. SIMPLE TOP NAV */}
    <div className="relative w-full max-w-4xl flex justify-between items-center z-20 shrink-0 border-b border-white/5 pb-6">
      <div className="flex flex-col gap-1">
        <p className="text-[10px] tracking-widest font-bold uppercase text-white">
          Omore Games
        </p>
        <div className="flex items-center gap-2">
           <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />
           <p className="text-[8px] text-zinc-200 uppercase">System Ready</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-[8px] text-zinc-200 uppercase mb-1">Loading Data</p>
        <div className="w-24 h-1 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-white transition-all duration-700" style={{ width: `${loading}%` }} />
        </div>
      </div>
    </div>

    {/* 3. MAIN CONTENT - Clean & Friendly */}
    <div className="relative z-10 w-full max-w-2xl flex flex-col flex-1 justify-center space-y-10">
      
      <header className="space-y-2">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white">
          Choose Your <span className="text-blue-400">Task</span>
        </h1>
        <p className="text-sm md:text-base text-zinc-400 font-medium">
          Select a game below to start your training session.
        </p>
      </header>

      {/* GAME SELECTION CARDS */}
      <div className="flex flex-col gap-4 w-full">
        {[
          { id: "01", title: "Remember Me", desc: "Complete the sequence to advance.", type: "vault" },
          { id: "02", title: "Brain Tease", desc: "Maintain accuracy under pressure.", type: "stroop" },
          { id: "03", title: "Quick Tap", desc: "Test your limits against the clock.", type: "schulte" },
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => start(item.type)}
            className="group relative w-full text-left transition-all duration-300"
          >
            <div className="relative p-6 md:p-8 bg-[#1a1d23] border border-white/5 rounded-2xl flex items-center justify-between group-hover:bg-[#23272f] group-hover:border-blue-400/50 transition-all">
              <div className="flex items-center gap-6">
                <div className="flex items-center justify-center w-12 h-12 bg-[#0f1115] rounded-xl text-zinc-200 group-hover:text-blue-400 font-bold text-sm border border-white/5 transition-colors">
                  {item.id}
                </div>
                <div className="flex flex-col">
                  <h3 className="text-xl md:text-2xl font-bold text-white">
                    {item.title}
                  </h3>
                  <p className="text-xs md:text-sm text-zinc-200">
                    {item.desc}
                  </p>
                </div>
              </div>
              
              <div className="opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300 text-blue-400">
                <ArrowRight size={20} />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* QUIT BUTTON */}
      <Link
        to="/game/start"
        className="group flex items-center gap-3 text-sm text-zinc-200 hover:text-white transition-all no-underline w-fit"
      >
        <span className="font-bold tracking-wide">[ Exit Game ]</span>
        <div className="w-8 h-[1px] bg-zinc-800 group-hover:w-12 group-hover:bg-red-500 transition-all" />
      </Link>
    </div>

    {/* 4. FOOTER */}
    <div className="relative w-full max-w-4xl flex justify-between opacity-40 shrink-0 text-[9px] font-bold text-zinc-200">
      <p>ESTABLISHED CONNECTION</p>
    </div>
  </div>
)}
        {status === "playing" && (
          <div className="h-full">
            {game === "vault" && <MemoryGame onComplete={handleResult} />}
            {game === "stroop" && <FocusGame onComplete={handleResult} />}
            {game === "schulte" && <SpeedGame onComplete={handleResult} />}
          </div>
        )}

{(status === "win" || status === "fail") && (
  <div className="absolute inset-0 z-[60] bg-black flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in duration-300">
    
    {/* 1. MAIN RESULT */}
    <div className="mb-6">
      {status === "win" ? (
        <>
          <Trophy className="w-20 h-20 text-blue-500 mx-auto mb-4 drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]" />
          <h1 className="text-6xl font-black italic uppercase tracking-tighter text-white">
            YOU WIN!
          </h1>
          <p className="text-blue-400 font-bold uppercase text-[10px] tracking-[0.3em] mt-2">
            Perfect Connection
          </p>
        </>
      ) : (
        <>
          <RefreshCcw className="w-20 h-20 text-red-600 mx-auto mb-4 drop-shadow-[0_0_15px_rgba(220,38,38,0.5)]" />
          <h1 className="text-6xl font-black italic uppercase tracking-tighter text-white">
            GAME OVER
          </h1>
          <p className="text-red-500 font-bold uppercase text-[10px] tracking-[0.3em] mt-2">
            Try Again
          </p>
        </>
      )}
    </div>

    {/* 2. SIMPLE REVIEW */}
    <div className="w-full max-w-xs bg-zinc-900/50 border border-white/10 rounded-3xl p-8 my-6 space-y-2">
      <h3 className="text-[10px] text-zinc-200 uppercase font-black tracking-widest border-b border-white/5 pb-3 mb-4">
        Your Score Card
      </h3>
      
      <p className="text-white text-xl font-black italic tracking-tight">
        {status === "win" ? "SUPER BRAIN" : "TOO SLOW"}
      </p>
      
      <p className="text-zinc-400 text-xs leading-relaxed font-bold uppercase">
    {status === "win" 
      ? "Amazing! Your eyes, brain, and fingers worked in perfect sync." 
      : "Your eyes were too slow, your brain got confused, or your fingers didn't tap in time."
    }
  </p>
    </div>

    {/* 3. BIG BUTTON */}
    <button
      onClick={() => setStatus("menu")}
      className="group relative mt-4 px-12 py-5 bg-white rounded-full transition-all active:scale-95 overflow-hidden"
    >
      <span className="relative z-10 text-black font-black uppercase text-sm tracking-widest">
        {status === "win" ? "CONTINUE" : "PLAY AGAIN"}
      </span>
    </button>

    <footer className="mt-12">
       <p className="text-[7px] text-zinc-800 font-bold uppercase tracking-[1em]">Omore.Games | End of Game</p>
    </footer>
  </div>
)}
      </main>
     
    </div>
  );
}

function MenuBtn({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full max-w-xs flex items-center justify-between p-5 bg-zinc-900/50 border border-white/5 rounded-2xl active:bg-blue-600 transition-all"
    >
      <div className="flex items-center gap-4">
        <div className="text-blue-500">{icon}</div>
        <span className="text-[11px] font-black uppercase tracking-widest">
          {label}
        </span>
      </div>
      <div className="text-[8px] font-bold text-zinc-600 uppercase">
        Load Protocol
      </div>
    </button>
  );
}
